
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { CodeEditor } from './CodeEditor';

export function WebhookPayloadTemplates() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState<any>(null);
  
  // Form state
  const [templateName, setTemplateName] = useState('');
  const [templateBody, setTemplateBody] = useState('{}');
  const [selectedEventType, setSelectedEventType] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  
  // Query all templates
  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['webhook-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhook_payload_templates')
        .select(`
          *,
          webhook_event_types (event_code)
        `)
        .order('template_name', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });
  
  // Query all event types for template selection
  const { data: eventTypes, isLoading: eventTypesLoading } = useQuery({
    queryKey: ['webhook-event-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhook_event_types')
        .select('*')
        .eq('is_active', true)
        .order('event_code', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });
  
  // Create or update template
  const mutation = useMutation({
    mutationFn: async (values: any) => {
      try {
        // Parse the JSON input to ensure it's valid
        const parsedTemplateBody = JSON.parse(values.templateBody);
        
        if (currentTemplate) {
          // Update
          const { data, error } = await supabase
            .from('webhook_payload_templates')
            .update({
              template_name: values.templateName,
              event_type_id: parseInt(values.selectedEventType),
              payload_template: parsedTemplateBody,
              is_default: values.isDefault,
            })
            .eq('template_id', currentTemplate.template_id)
            .select();
          
          if (error) throw error;
          return data;
        } else {
          // Create
          const { data, error } = await supabase
            .from('webhook_payload_templates')
            .insert({
              template_name: values.templateName,
              event_type_id: parseInt(values.selectedEventType),
              payload_template: parsedTemplateBody,
              is_default: values.isDefault,
            })
            .select();
          
          if (error) throw error;
          return data;
        }
      } catch (error) {
        throw new Error('Invalid JSON in template body');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-templates'] });
      toast({
        title: `Template ${currentTemplate ? 'Updated' : 'Created'}`,
        description: `Successfully ${currentTemplate ? 'updated' : 'created'} webhook payload template.`,
      });
      resetForm();
      setDialogOpen(false);
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  
  // Handle delete template
  const deleteMutation = useMutation({
    mutationFn: async (templateId: number) => {
      const { error } = await supabase
        .from('webhook_payload_templates')
        .delete()
        .eq('template_id', templateId);
      
      if (error) throw error;
      return templateId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-templates'] });
      toast({
        title: 'Template Deleted',
        description: 'The webhook payload template has been deleted.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  
  // Reset form
  const resetForm = () => {
    setTemplateName('');
    setTemplateBody('{}');
    setSelectedEventType('');
    setIsDefault(false);
    setCurrentTemplate(null);
  };
  
  // Open create form
  const handleCreate = () => {
    resetForm();
    setDialogOpen(true);
  };
  
  // Open edit form
  const handleEdit = (item: any) => {
    setCurrentTemplate(item);
    setTemplateName(item.template_name);
    setTemplateBody(JSON.stringify(item.payload_template, null, 2));
    setSelectedEventType(item.event_type_id.toString());
    setIsDefault(item.is_default || false);
    setDialogOpen(true);
  };
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!templateName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Template name is required.',
        variant: 'destructive',
      });
      return;
    }
    
    if (!selectedEventType) {
      toast({
        title: 'Validation Error',
        description: 'Please select an event type.',
        variant: 'destructive',
      });
      return;
    }
    
    mutation.mutate({
      templateName,
      templateBody,
      selectedEventType,
      isDefault,
    });
  };
  
  // Handle delete confirmation
  const handleDelete = (templateId: number) => {
    if (confirm('Are you sure you want to delete this template?')) {
      deleteMutation.mutate(templateId);
    }
  };
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Webhook Payload Templates</CardTitle>
        <Button onClick={handleCreate}>Add Template</Button>
      </CardHeader>
      
      <CardContent>
        {templatesLoading ? (
          <div>Loading templates...</div>
        ) : (
          <ScrollArea className="h-[60vh] w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template Name</TableHead>
                  <TableHead>Event Type</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center">
                      No templates found. Create one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  templates?.map((template) => (
                    <TableRow key={template.template_id}>
                      <TableCell className="font-medium">{template.template_name}</TableCell>
                      <TableCell>{template.webhook_event_types.event_code}</TableCell>
                      <TableCell>{template.is_default ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(template)}>
                            Edit
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-red-500" 
                            onClick={() => handleDelete(template.template_id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>
      
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {currentTemplate ? 'Edit' : 'Create'} Webhook Payload Template
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="template-name">Template Name</Label>
                <Input
                  id="template-name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Order confirmation template"
                  required
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="event-type">Event Type</Label>
                <Select 
                  value={selectedEventType} 
                  onValueChange={setSelectedEventType}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Event Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {eventTypes?.map((type) => (
                      <SelectItem key={type.event_type_id} value={type.event_type_id.toString()}>
                        {type.event_code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="template-body">Template Body (JSON)</Label>
                <CodeEditor
                  value={templateBody}
                  onChange={setTemplateBody}
                  language="json"
                  height="300px"
                />
                <p className="text-sm text-muted-foreground">
                  Define the JSON structure for the webhook payload. Use <code>{"{{variable_name}}"}</code> for placeholders.
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch 
                  id="is-default" 
                  checked={isDefault}
                  onCheckedChange={setIsDefault}
                />
                <Label htmlFor="is-default">Default Template for this Event Type</Label>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving...' : 'Save Template'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
